import React, { useEffect, useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import { Button, TextInput, Dropdown } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import EditorJSText from "@components/EditorJSText/EditorJSText";
// HTML editor tab + formatter commented out: raw HTML is edited via the
// EditorJS "raw" block (CodeMirror) inside the visual editor instead.
// import CodeMirror from "@uiw/react-codemirror";
// import { html } from "@codemirror/lang-html";
// import { EditorView } from "@codemirror/view";
// import prettier from "prettier";
// import prettierParserHtml from "prettier/parser-html";
import addIcon from "@icons/ic-add-blue.svg";
import { fetchCountryParameters } from "../../GeneralParameters/GeneralParametersList/GeneralParametersList.api";
import { wrapEmailHtml } from "../emailBaseStyles";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import {
  MailTokenViewContainer,
  Header,
  Title,
  Description,
  HeaderActions,
  TabContainer,
  Tab,
  AddTabButton,
  CountryDropdown,
  CountryDropdownItem,
  ContentContainer,
  FormSection,
  FormRow,
  SidebarContainer,
  VariablesSection,
  VariablesList,
  VariableItem,
  VariableCategory,
  ButtonWrapper,
  GridRow,
  TreeNode,
  TreeNodeHeader,
  TreeNodeIcon,
  TreeNodeLabel,
  TreeNodeBadge,
  TreeNodeChildren,
  // EditorModeTabs,
  // EditorModeTab,
} from "./AddEditMailTemplate.styled";

import "./AddEditMailTemplate.scss";
import NotFound from "@components/Errors/NotFound";

/**
 * MailToken View/Edit Page
 * Path - /administration/emails/view/:id
 * @component
 */
const AddEditMailTemplate = (props) => {
  const { t } = props;
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showSendExampleModal, setShowSendExampleModal] = useState(false);
  const [sendExampleEmail, setSendExampleEmail] = useState("");
  const [sendExampleError, setSendExampleError] = useState("");
  const [sendingExample, setSendingExample] = useState(false);
  const [mailToken, setMailToken] = useState(null);
  const [activeCountry, setActiveCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [templates, setTemplates] = useState({});
  const [availableVariables, setAvailableVariables] = useState({});
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const lastFocusedFieldRef = useRef(null);
  const dropdownRef = useRef(null);
  const editorRef = useRef(null);

  const [formErrors, setFormErrors] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [generalTemplates, setGeneralTemplates] = useState({});
  const generalTemplatesCacheRef = useRef(null); // Cache for all countries' general templates
  // const [bodyEditorMode, setBodyEditorMode] = useState("visual"); // "visual" | "html"
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    fetchMailToken();
    fetchCountries();
    fetchGeneralTemplates(); // Fetch once on component mount
  }, [id]);

  // Set general templates from cache when activeCountry changes
  useEffect(() => {
    if (activeCountry && generalTemplatesCacheRef.current) {
      // Use cached data for the current country
      const cachedHeader =
        generalTemplatesCacheRef.current.headerTemplates?.[activeCountry] || "";
      const cachedFooter =
        generalTemplatesCacheRef.current.footerTemplates?.[activeCountry] || "";
      setGeneralTemplates({
        GENERAL_HEADER: cachedHeader,
        GENERAL_FOOTER: cachedFooter,
      });
    }
  }, [activeCountry]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchCountries = () => {
    axios
      .get("/general/parameter")
      .then((response) => {
        const countryList = response.data.countryList || [];
        setCountries(countryList);
      })
      .catch((error) => {
        console.error("Error fetching countries:", error);
        // Set default countries if API fails
        const defaultCountries = [
          { id: 2, name: "France", platform: 1 },
          { id: 1, name: "United Kingdom", platform: 1 },
          { id: 6, name: "Germany", platform: 1 },
          { id: 3, name: "Poland", platform: 1 },
        ];
        setCountries(defaultCountries);
      });
  };

  // Fetch GENERAL_HEADER and GENERAL_FOOTER templates once and cache for all countries
  const fetchGeneralTemplates = async () => {
    // Skip if already cached
    if (generalTemplatesCacheRef.current) {
      return;
    }

    try {
      // Fetch GENERAL_HEADER and GENERAL_FOOTER in parallel
      const [headerResponse, footerResponse] = await Promise.all([
        axios.get(`/general/mailTokens/byName/GENERAL_HEADER`),
        axios.get(`/general/mailTokens/byName/GENERAL_FOOTER`),
      ]);

      const headerData =
        headerResponse.data.mailTokens?.[0] || headerResponse.data;
      const footerData =
        footerResponse.data.mailTokens?.[0] || footerResponse.data;

      // Build cache mapping countryId -> template body
      const headerTemplates = {};
      const footerTemplates = {};

      if (headerData.templates && Array.isArray(headerData.templates)) {
        headerData.templates.forEach((t) => {
          if (t.country?.id && t.body) {
            headerTemplates[t.country.id] = t.body;
          }
        });
      }

      if (footerData.templates && Array.isArray(footerData.templates)) {
        footerData.templates.forEach((t) => {
          if (t.country?.id && t.body) {
            footerTemplates[t.country.id] = t.body;
          }
        });
      }

      // Store in cache ref
      generalTemplatesCacheRef.current = { headerTemplates, footerTemplates };

      // Set current country's templates
      if (activeCountry) {
        setGeneralTemplates({
          GENERAL_HEADER: headerTemplates[activeCountry] || "",
          GENERAL_FOOTER: footerTemplates[activeCountry] || "",
        });
      }
    } catch (error) {
      console.error("Error fetching general templates:", error);
      generalTemplatesCacheRef.current = {
        headerTemplates: {},
        footerTemplates: {},
      };
      setGeneralTemplates({ GENERAL_HEADER: "", GENERAL_FOOTER: "" });
    }
  };

  // Commented out with the HTML editor tab - raw HTML now lives in EditorJS
  // "raw" blocks, so visual mode is always safe.
  // const isEditorJsContent = (value) => {
  //   const trimmed = (value || "").trim();
  //   return trimmed.startsWith("{") || trimmed.startsWith("[");
  // };

  const fetchMailToken = () => {
    setLoading(true);
    axios
      .get(`/general/mailTokens/${id}`)
      .then((response) => {
        const data = response.data.mailTokens?.[0] || response.data;
        setMailToken(data);

        // Parse templates by country
        const templatesByCountry = {};
        if (data.templates && Array.isArray(data.templates)) {
          data.templates.forEach((template) => {
            // Map API field names to frontend field names
            templatesByCountry[template.country.id] = {
              ...template,
              toAddress: template.recipients ?? template.toAddress ?? "",
              cci: template.bcc ?? template.cci ?? "",
            };
          });
        }
        setTemplates(templatesByCountry);

        // Set first template country as active if exists
        const templateCountryIds = Object.keys(templatesByCountry);
        if (templateCountryIds.length > 0 && !activeCountry) {
          const firstCountryId = parseInt(templateCountryIds[0]);
          setActiveCountry(firstCountryId);
          // HTML tab commented out - always visual mode (raw blocks keep HTML)
          // setBodyEditorMode(
          //   isEditorJsContent(templatesByCountry[firstCountryId]?.body)
          //     ? "visual"
          //     : "html"
          // );
        }

        // Parse available variables from jsonExample
        if (data.jsonExample) {
          try {
            const variables = JSON.parse(data.jsonExample);
            setAvailableVariables(variables);
          } catch (e) {
            console.error("Error parsing jsonExample:", e);
          }
        }

        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching mail token:", error);
        setLoading(false);
        if (error?.response?.status === 404) {
          setIsNotFound(true);
          return;
        }
      });
  };

  const handleTabChange = (countryId) => {
    setActiveCountry(countryId);
    // HTML tab commented out - always visual mode (raw blocks keep HTML)
    // setBodyEditorMode(
    //   isEditorJsContent(templates[countryId]?.body) ? "visual" : "html"
    // );
  };

  const handleAddCountry = (countryId) => {
    // Create empty template for the country
    setTemplates({
      ...templates,
      [countryId]: {
        country: { id: countryId },
        subject: "",
        toAddress: "",
        cc: "",
        cci: "",
        body: "",
      },
    });
    setActiveCountry(countryId);
    setShowCountryDropdown(false);
  };

  const handleFieldChange = (field, value) => {
    // Use functional update to avoid stale state issues
    setTemplates((prevTemplates) => {
      const currentTemplate = prevTemplates[activeCountry] || {};
      return {
        ...prevTemplates,
        [activeCountry]: {
          ...currentTemplate,
          [field]: value,
        },
      };
    });

    // Clear error for this field
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [`${activeCountry}_${field}`]: "",
    }));
  };

  const handleVariableClick = (
    event,
    variable,
    isObject = false,
    isRecipientVar = false,
    forceTildeFormat = false,
    isGeneralVar = false
  ) => {
    // Prevent default and stop propagation to avoid any side effects
    event.preventDefault();
    event.stopPropagation();

    // Recipient fields that can accept recipient variables
    const recipientFields = ["toAddress", "cc", "cci"];
    // Fields that can accept application variables
    const appVarFields = ["subject", "body"];

    // Use focusedField or fall back to lastFocusedFieldRef (in case focus was lost)
    const currentFocus = focusedField || lastFocusedFieldRef.current;

    // General variables (GENERAL_HEADER, GENERAL_FOOTER) should only insert when body is focused
    if (isGeneralVar && currentFocus !== "body") {
      return; // Do nothing if body is not focused
    }

    // Determine target field based on variable type and current focus
    let targetField = currentFocus;

    if (isRecipientVar) {
      // Recipient variables should only go to recipient fields (toAddress, cc, cci)
      // If current focused field is not a recipient field, default to toAddress
      if (!recipientFields.includes(currentFocus)) {
        targetField = "toAddress";
      }
    } else {
      // App content variables can go to subject or body
      // If focused on subject, insert there; otherwise default to body
      if (!currentFocus || !appVarFields.includes(currentFocus)) {
        targetField = "body";
      }
      // If currentFocus is 'subject' or 'body', targetField stays as currentFocus
    }

    // Format variable:
    // - Recipient variables use ~VARIABLE~ format
    // - App content: {category.variable} for simple, [{category.variable}] for objects
    let formattedVariable;
    if (isRecipientVar || forceTildeFormat) {
      formattedVariable = `~${variable}~`;
    } else {
      formattedVariable = isObject ? `[{${variable}}]` : `{${variable}}`;
    }

    // Insert at cursor position based on target field
    if (targetField === "body" && editorRef.current) {
      editorRef.current.insertVariable(formattedVariable);
    } else {
      // For text inputs, append the variable to the current value
      setTemplates((prevTemplates) => {
        const currentTemplate = prevTemplates[activeCountry] || {};
        const currentValue = currentTemplate[targetField] || "";
        const newValue = currentValue + formattedVariable;

        return {
          ...prevTemplates,
          [activeCountry]: {
            ...currentTemplate,
            [targetField]: newValue,
          },
        };
      });
    }
  };

  useEffect(() => {}, [templates]);

  const handleFieldFocus = (fieldName) => {
    setFocusedField(fieldName);
    lastFocusedFieldRef.current = fieldName;
  };

  // HTML editor tab commented out - kept for easy re-enable if a separate
  // full-body HTML mode is ever needed again. Raw HTML is edited in the
  // visual editor via the "raw" block (CodeMirror).
  /*
  const handleBodyEditorModeChange = async (mode) => {
    if (mode === bodyEditorMode) return;

    if (mode === "visual" && !isEditorJsContent(currentTemplate.body)) {
      const confirmed = window.confirm(
        "This template contains custom HTML. Switching to the visual editor will convert it to blocks and may lose the design. Continue?"
      );
      if (!confirmed) return;
    }

    if (mode === "html") {
      let bodyValue = currentTemplate.body || "";

      if (editorRef.current && editorRef.current.getEditorInstance) {
        try {
          const editorInstance = editorRef.current.getEditorInstance();
          if (editorInstance) {
            const savedData = await editorInstance.save();
            if (savedData) {
              bodyValue = JSON.stringify(savedData);
            }
          }
        } catch (error) {
          console.error("Error reading editor content:", error);
        }
      }

      const trimmed = bodyValue.trim();
      const isJSON = trimmed.startsWith("{") || trimmed.startsWith("[");
      const htmlValue = isJSON ? convertEditorJSToHTML(bodyValue) : bodyValue;
      handleFieldChange("body", htmlValue);
    }

    setBodyEditorMode(mode);
  };

  const handleFormatHtml = () => {
    const bodyValue = currentTemplate.body || "";
    if (!bodyValue.trim()) return;

    try {
      const formatted = prettier.format(bodyValue, {
        parser: "html",
        plugins: [prettierParserHtml],
        printWidth: 100000,
        htmlWhitespaceSensitivity: "ignore",
        endOfLine: "lf",
      });
      handleFieldChange("body", formatted.trim());
    } catch (error) {
      console.error("Error formatting HTML:", error);
    }
  };
  */

  const validateForm = () => {
    const errors = {};
    const currentTemplate = templates[activeCountry];

    if (!currentTemplate?.subject || currentTemplate.subject.trim() === "") {
      errors[`${activeCountry}_subject`] = t("GENERAL_MANDATORY_ERROR");
    }

    if (
      !currentTemplate?.toAddress ||
      currentTemplate.toAddress.trim() === ""
    ) {
      errors[`${activeCountry}_toAddress`] = t("GENERAL_MANDATORY_ERROR");
    }

    if (!currentTemplate?.body || currentTemplate.body.trim() === "") {
      errors[`${activeCountry}_body`] = t("GENERAL_MANDATORY_ERROR");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    const currentTemplate = templates[activeCountry] || {};
    const templateData = {
      countryId: activeCountry,
      subject: currentTemplate.subject ?? "",
      toAddress: currentTemplate.toAddress ?? "",
      cc: currentTemplate.cc ?? "",
      cci: currentTemplate.cci ?? "",
      body: currentTemplate.body ?? "",
      isActive: 1,
    };

    // Use nested API endpoints: POST /general/mailTokens/{mailTokenId}/mailTemplates or PUT /general/mailTokens/{mailTokenId}/mailTemplates/{mailTemplateId}
    const apiCall = currentTemplate.id
      ? axios.put(
          `/general/mailTokens/${id}/mailTemplates/${currentTemplate.id}`,
          templateData
        )
      : axios.post(`/general/mailTokens/${id}/mailTemplates`, templateData);

    apiCall
      .then((response) => {
        setSaving(false);
        // Update local template with returned data (including new ID for created templates)
        if (response.data?.data?.id) {
          setTemplates((prev) => ({
            ...prev,
            [activeCountry]: {
              ...prev[activeCountry],
              id: response.data.data.id,
              ...response.data.data,
            },
          }));
        }
        fetchMailToken(); // Refresh data
      })
      .catch((error) => {
        console.error("Error saving template:", error);
        setSaving(false);
      });
  };

  // Convert EditorJS JSON to HTML for preview
  const convertEditorJSToHTML = (jsonString) => {
    if (!jsonString) return "";

    // Check if it's already HTML (starts with < or doesn't look like JSON)
    const trimmed = jsonString.trim();
    if (
      trimmed.startsWith("<") ||
      (!trimmed.startsWith("{") && !trimmed.startsWith("["))
    ) {
      // Already HTML, return as-is
      return jsonString;
    }

    try {
      const data = JSON.parse(jsonString);
      if (!data || !data.blocks) return jsonString; // Return as-is if not EditorJS format

      // Helper to get alignment style
      const getAlignmentStyle = (block) => {
        const alignment = block.tunes?.alignmentTune?.alignment || "left";
        return alignment !== "left" ? `text-align: ${alignment};` : "";
      };

      // Helper to build style attribute
      const buildStyleAttr = (styles) => {
        return styles ? ` style="${styles}"` : "";
      };

      return data.blocks
        .map((block) => {
          const alignStyle = getAlignmentStyle(block);

          switch (block.type) {
            case "header": {
              const mergedStyle = [block.data.style, alignStyle]
                .filter(Boolean)
                .join("; ")
                .trim();
              return `<h${block.data.level}${buildStyleAttr(mergedStyle)}>${
                block.data.text
              }</h${block.data.level}>`;
            }
            case "paragraph": {
              const mergedStyle = [block.data.style, alignStyle]
                .filter(Boolean)
                .join("; ")
                .trim();
              return `<p${buildStyleAttr(mergedStyle)}>${block.data.text}</p>`;
            }
            case "list":
              const tag = block.data.style === "ordered" ? "ol" : "ul";
              const items = block.data.items
                .map((item) => {
                  // Handle both string items and object items (newer EditorJS versions)
                  const content =
                    typeof item === "string"
                      ? item
                      : item.content || item.text || "";
                  return `<li>${content}</li>`;
                })
                .join("");
              return `<${tag}>${items}</${tag}>`;
            case "table": {
              const withHeadings = !!block.data.withHeadings;
              const content = block.data.content || [];
              const bodyRows = withHeadings ? content.slice(1) : content;
              const headerRow = withHeadings ? content[0] : null;
              const colWidths = block.data.colWidths || [];
              const rowHeights = block.data.rowHeights || [];
              const headerBgColor = block.data.headerBgColor || "";
              const cellBgColors = block.data.cellBgColors || [];
              const borderColor = block.data.borderColor || "#ddd";
              const borderWidth = block.data.borderWidth || "1px";
              const borderStyle = block.data.borderStyle || "solid";
              const borderCss = `${borderWidth} ${borderStyle} ${borderColor}`;
              const tableStyle = `border-collapse: collapse; width: 100%;`;

              const headHtml = headerRow
                ? `<thead><tr${
                    rowHeights[0] ? ` height="${rowHeights[0]}"` : ""
                  }>${headerRow
                    .map((cell, i) => {
                      const w = colWidths[i] ? ` width="${colWidths[i]}"` : "";
                      const cellBg = cellBgColors[0]?.[i];
                      let bg;
                      if (cellBg) {
                        bg = ` style="border: ${borderWidth} ${borderStyle} ${cellBg}; background-color: ${cellBg};"`;
                      } else if (headerBgColor) {
                        bg = ` style="border: ${borderCss}; background-color: ${headerBgColor}; color: #fff; font-weight: bold;"`;
                      } else {
                        bg = ` style="border: ${borderCss};"`;
                      }
                      return `<th${w}${bg}>${cell}</th>`;
                    })
                    .join("")}</tr></thead>`
                : "";
              const bodyHtml = bodyRows
                .map((row, rIdx) => {
                  const dataRowIdx = withHeadings ? rIdx + 1 : rIdx;
                  const h = rowHeights[dataRowIdx]
                    ? ` height="${rowHeights[dataRowIdx]}"`
                    : "";
                  return `<tr${h}>${row
                    .map((cell, cIdx) => {
                      const w = colWidths[cIdx]
                        ? ` width="${colWidths[cIdx]}"`
                        : "";
                      const cellBg = cellBgColors[dataRowIdx]?.[cIdx];
                      const bgStyle = cellBg
                        ? `background-color: ${cellBg}; `
                        : "";
                      return `<td${w} style="${bgStyle}border: ${borderCss};">${cell}</td>`;
                    })
                    .join("")}</tr>`;
                })
                .join("");
              return `<table style="${tableStyle}">${headHtml}<tbody>${bodyHtml}</tbody></table>`;
            }
            case "linkTool":
              return `<a href="${block.data.link}" target="_blank">${
                block.data.meta?.title || block.data.link
              }</a>`;
            case "image":
              const imgCaption = block.data.caption
                ? `<figcaption style="text-align: center; font-size: 14px; color: #666; margin-top: 8px;">${block.data.caption}</figcaption>`
                : "";
              // Support both old format (file.url) and new format (url directly)
              const imgUrl = block.data.url || block.data.file?.url || "";
              const imgWidth = block.data.width || "100%";
              const imgAlignment = block.data.alignment || "center";
              return `<figure style="margin: 10px 0; text-align: ${imgAlignment};"><img src="${imgUrl}" alt="${
                block.data.caption || ""
              }" style="width: ${imgWidth}; max-width: 100%; height: auto;" />${imgCaption}</figure>`;
            case "imageGallery":
              const galleryImages = block.data.images || [];
              const galleryLayout =
                block.data.layout === "auto"
                  ? Math.min(galleryImages.length || 1, 4)
                  : parseInt(block.data.layout);
              const galleryGap = block.data.gap || "10px";
              const galleryImagesHtml = galleryImages
                .map((img) => {
                  const galCaption = img.caption
                    ? `<figcaption style="text-align: center; font-size: 12px; color: #666; padding: 5px;">${img.caption}</figcaption>`
                    : "";
                  return `<figure style="margin: 0;"><img src="${
                    img.url
                  }" alt="${
                    img.caption || ""
                  }" style="width: 100%; height: auto; display: block;" />${galCaption}</figure>`;
                })
                .join("");
              return `<div style="display: grid; grid-template-columns: repeat(${galleryLayout}, 1fr); gap: ${galleryGap}; margin: 10px 0;">${galleryImagesHtml}</div>`;
            case "delimiter":
              return `<hr style="border: none; border-top: 2px solid #ccc; margin: 20px 0;" />`;
            case "raw":
              return block.data.html || "";
            case "quote":
              const quoteCaption = block.data.caption
                ? `<cite>— ${block.data.caption}</cite>`
                : "";
              const quoteStyles =
                `border-left: 3px solid #ccc; padding-left: 15px; margin: 10px 0; font-style: italic; ${alignStyle}`.trim();
              return `<blockquote style="${quoteStyles}"><p>${block.data.text}</p>${quoteCaption}</blockquote>`;
            default:
              return `<p>${block.data?.text || ""}</p>`;
          }
        })
        .join("");
    } catch (e) {
      // If not valid JSON, return as-is (might be HTML already)
      return jsonString;
    }
  };

  // Replace template variables with example data from jsonExample
  const replaceVariablesWithExampleData = (content, exampleData) => {
    if (!content || !exampleData) return content;

    let result = content;

    // Helper function to get nested value from object using dot notation
    const getNestedValue = (obj, path) => {
      return path.split(".").reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    };

    // Helper function to render array data in list format
    const renderArrayAsList = (arr, itemTemplate, arrayName) => {
      // Escape special regex characters in arrayName
      const escapedArrayName = arrayName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      return arr
        .map((item) => {
          let itemHtml = itemTemplate;
          if (typeof item === "object" && item !== null) {
            // Replace item properties in template
            Object.keys(item).forEach((key) => {
              const value =
                item[key] !== null && item[key] !== undefined
                  ? String(item[key])
                  : "";
              // Replace %arrayName.property% format. Tolerate optional extra
              // path prefixes, e.g. {quote.quoteItems.name} when arrayName is
              // "quoteItems".
              const prefixedKey = `(?:[a-zA-Z0-9_]+\\.)*${escapedArrayName}\\.${key}`;
              itemHtml = itemHtml.replace(
                new RegExp(`%${prefixedKey}%`, "gi"),
                value
              );
              itemHtml = itemHtml.replace(
                new RegExp(`\\{\\{\\s*${prefixedKey}\\s*\\}\\}`, "gi"),
                value
              );
              itemHtml = itemHtml.replace(
                new RegExp(`\\{${prefixedKey}\\}`, "gi"),
                value
              );
              // Also replace %property% format (without array name prefix)
              itemHtml = itemHtml.replace(new RegExp(`%${key}%`, "gi"), value);
              itemHtml = itemHtml.replace(
                new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "gi"),
                value
              );
              itemHtml = itemHtml.replace(
                new RegExp(`\\{${key}\\}`, "gi"),
                value
              );
            });
          } else {
            // Simple value - replace generic placeholders
            itemHtml = itemHtml.replace(/%item%/gi, String(item));
            itemHtml = itemHtml.replace(/\{\{\s*item\s*\}\}/gi, String(item));
            itemHtml = itemHtml.replace(/\{item\}/gi, String(item));
          }
          return itemHtml;
        })
        .join("");
    };

    // Helper function to replace nested object properties recursively
    const replaceNestedProperties = (html, obj, basePath) => {
      let result = html;
      const escapedBasePath = basePath.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (
          value !== null &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          // Recursively handle nested objects
          result = replaceNestedProperties(result, value, `${basePath}.${key}`);
        } else {
          const stringValue =
            value !== null && value !== undefined ? String(value) : "";
          // Replace {basePath.key} format. Tolerate optional extra path
          // prefixes, e.g. {quote.quoteItems.unit} when basePath is
          // "quoteItems".
          const prefixedPath = `(?:[a-zA-Z0-9_]+\.)*${escapedBasePath}\.${key}`;
          result = result.replace(
            new RegExp(`\\{${prefixedPath}\\}`, "gi"),
            stringValue
          );
          // Replace [{basePath.key}] format (for objects)
          result = result.replace(
            new RegExp(`\\[\\{${prefixedPath}\\}\\]`, "gi"),
            stringValue
          );
        }
      });
      return result;
    };

    // Helper function to render array data in table rows
    const renderArrayAsTableRows = (arr, rowTemplate, arrayName) => {
      // Escape special regex characters in arrayName
      const escapedArrayName = arrayName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      return arr
        .map((item) => {
          let rowHtml = rowTemplate;
          if (typeof item === "object" && item !== null) {
            // First, handle nested objects recursively
            rowHtml = replaceNestedProperties(rowHtml, item, arrayName);

            // Then handle simple properties
            Object.keys(item).forEach((key) => {
              const value = item[key];
              // Skip nested objects (already handled)
              if (value !== null && typeof value === "object") return;

              const stringValue =
                value !== null && value !== undefined ? String(value) : "";
              // Replace %arrayName.property% format (e.g., %discussionComments.date%).
              // Tolerate optional extra path prefixes, e.g.
              // {quote.quoteItems.sellingUnit} when arrayName is "quoteItems".
              const prefixedKey = `(?:[a-zA-Z0-9_]+\.)*${escapedArrayName}\.${key}`;
              rowHtml = rowHtml.replace(
                new RegExp(`%${prefixedKey}%`, "gi"),
                stringValue
              );
              rowHtml = rowHtml.replace(
                new RegExp(`\\{\\{\\s*${prefixedKey}\\s*\\}\\}`, "gi"),
                stringValue
              );
              rowHtml = rowHtml.replace(
                new RegExp(`\\{${prefixedKey}\\}`, "gi"),
                stringValue
              );
              // Also replace %property% format (without array name prefix)
              rowHtml = rowHtml.replace(
                new RegExp(`%${key}%`, "gi"),
                stringValue
              );
              rowHtml = rowHtml.replace(
                new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "gi"),
                stringValue
              );
              rowHtml = rowHtml.replace(
                new RegExp(`\\{${key}\\}`, "gi"),
                stringValue
              );
            });
          }
          return rowHtml;
        })
        .join("");
    };

    // Process arrays in lists (<ul> or <ol> containing array variable)
    // Pattern: <ul>...<li>%arrayName.property%</li>...</ul>
    const listRegex = /<(ul|ol)[^>]*>([\s\S]*?)<\/\1>/gi;
    result = result.replace(listRegex, (match, listTag, listContent) => {
      // Check if list item contains array variable pattern
      const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/i;
      const liMatch = listContent.match(liRegex);

      if (liMatch) {
        const liTemplate = liMatch[0];
        const liContent = liMatch[1];

        // Find array variable in list item (e.g., %items.name% or %products%).
        // Scan the tag-stripped text so attribute values (e.g. width="23.5%")
        // can't produce false %...% matches.
        const liText = liContent.replace(/<[^>]+>/g, " ");
        const varMatch =
          liText.match(/%([^%]+)%/) ||
          liText.match(/\{\{([^}]+)\}\}/) ||
          liText.match(/\{([^}]+)\}/);

        if (varMatch) {
          const varPath = varMatch[1].trim();
          const arrayName = varPath.split(".")[0];
          const arrayData = getNestedValue(exampleData, arrayName);

          if (Array.isArray(arrayData) && arrayData.length > 0) {
            const renderedItems = renderArrayAsList(
              arrayData,
              liTemplate,
              arrayName
            );
            return `<${listTag}>${renderedItems}</${listTag}>`;
          }
        }
      }
      return match;
    });

    // Process arrays in tables (<table> containing array variable in <tr>)
    // Pattern: <table>...<tr><td>%arrayName.property%</td></tr>...</table>
    const tableRegex = /<table[^>]*>([\s\S]*?)<\/table>/gi;
    result = result.replace(tableRegex, (match, tableContent) => {
      // Find tbody or use table content directly
      const tbodyMatch = tableContent.match(/<tbody[^>]*>([\s\S]*?)<\/tbody>/i);
      const bodyContent = tbodyMatch ? tbodyMatch[1] : tableContent;

      // Find data rows (skip header row - first tr or tr in thead)
      const theadMatch = tableContent.match(/<thead[^>]*>([\s\S]*?)<\/thead>/i);
      const headerRow = theadMatch ? "" : "";

      // Find template row with variables
      const trRegex = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
      const rows = bodyContent.match(trRegex);

      if (rows && rows.length > 0) {
        // Find the row that contains array variables. Scan the tag-stripped
        // text so attribute values (e.g. width="23.5%" from resized columns)
        // can't produce false %...% matches.
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const rowText = row.replace(/<[^>]+>/g, " ");
          const varMatch =
            rowText.match(/%([^%<>]+)%/) ||
            rowText.match(/\{\{([^}]+)\}\}/) ||
            rowText.match(/\{([^}]+)\}/);

          if (varMatch) {
            const varPath = varMatch[1].trim();
            const arrayName = varPath.split(".")[0];
            const arrayData = getNestedValue(exampleData, arrayName);

            if (Array.isArray(arrayData) && arrayData.length > 0) {
              const renderedRows = renderArrayAsTableRows(
                arrayData,
                row,
                arrayName
              );
              // Replace the template row with rendered rows
              const newBodyContent = bodyContent.replace(row, renderedRows);

              if (tbodyMatch) {
                return match.replace(tbodyMatch[1], newBodyContent);
              } else {
                return match.replace(bodyContent, newBodyContent);
              }
            }
          }
        }
      }
      return match;
    });

    // Helper function to replace variables recursively for nested objects
    const replaceFromObject = (text, data, prefix = "") => {
      if (!data || typeof data !== "object") return text;

      Object.keys(data).forEach((key) => {
        const value = data[key];
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (Array.isArray(value)) {
          // Arrays should be handled by list/table processing above
          // Only replace %arrayName% (without property) with joined values for non-list/table contexts
          const displayValue = value
            .map((item) => {
              if (typeof item === "object" && item !== null) {
                return Object.values(item).join(" - ");
              }
              return String(item);
            })
            .join(", ");

          // Only replace exact %arrayName% pattern (not %arrayName.property%)
          const percentRegex = new RegExp(`%${fullKey}%(?!\\.)`, "gi");
          text = text.replace(percentRegex, displayValue);

          const doubleBraceRegex = new RegExp(
            `\\{\\{\\s*${fullKey}\\s*\\}\\}(?!\\.)`,
            "gi"
          );
          text = text.replace(doubleBraceRegex, displayValue);

          const singleBraceRegex = new RegExp(`\\{${fullKey}\\}(?!\\.)`, "gi");
          text = text.replace(singleBraceRegex, displayValue);

          // Do NOT replace %arrayName.property% patterns here - they should be handled by list/table processing
        } else if (value !== null && typeof value === "object") {
          // Recursively handle nested objects
          text = replaceFromObject(text, value, fullKey);
        } else {
          // Replace various variable formats: %key%, {{key}}, {key}
          const displayValue =
            value !== null && value !== undefined ? String(value) : "";

          // Replace %variable% format
          const percentRegex = new RegExp(`%${fullKey}%`, "gi");
          text = text.replace(percentRegex, displayValue);

          // Replace {{variable}} format
          const doubleBraceRegex = new RegExp(
            `\\{\\{\\s*${fullKey}\\s*\\}\\}`,
            "gi"
          );
          text = text.replace(doubleBraceRegex, displayValue);

          // Replace {variable} format
          const singleBraceRegex = new RegExp(`\\{${fullKey}\\}`, "gi");
          text = text.replace(singleBraceRegex, displayValue);

          // Also try without prefix for top-level keys
          if (prefix) {
            const simplePercentRegex = new RegExp(`%${key}%`, "gi");
            text = text.replace(simplePercentRegex, displayValue);

            const simpleDoubleBraceRegex = new RegExp(
              `\\{\\{\\s*${key}\\s*\\}\\}`,
              "gi"
            );
            text = text.replace(simpleDoubleBraceRegex, displayValue);
          }
        }
      });

      return text;
    };

    result = replaceFromObject(result, exampleData);

    return result;
  };

  // Fetch AccountancyEmail/AccountancyPhone general parameters for a given
  // country, preferring country-specific values and falling back to the
  // country-agnostic parameter (country_id = NULL).
  const getAccountancyValues = async (countryId) => {
    const result = { accountancyEmail: "", accountancyPhone: "" };
    if (!countryId) return result;

    try {
      const response = await fetchCountryParameters({
        codes: ["AccountancyEmail", "AccountancyPhone"],
        countries: [Number(countryId)],
        limit: -1,
        page: 1,
      });
      const parameters = response.countryParameters || [];

      ["AccountancyEmail", "AccountancyPhone"].forEach((code) => {
        const key =
          code === "AccountancyEmail" ? "accountancyEmail" : "accountancyPhone";
        const activeParams = parameters.filter((p) => p.isActive !== false);
        const specific = activeParams.find(
          (p) =>
            p.code === code &&
            p.country &&
            Number(p.country.id) === Number(countryId)
        );
        const fallback = activeParams.find(
          (p) => p.code === code && !p.country
        );
        const param = specific || fallback;
        if (param && param.value !== null && param.value !== undefined) {
          result[key] = String(param.value);
        }
      });
    } catch (error) {
      console.error("Error fetching accountancy general parameters:", error);
    }

    return result;
  };

  const handlePreview = async () => {
    const currentTemplate = templates[activeCountry];

    // Get the original body content from template
    const originalBody = currentTemplate?.body || "";
    const originalTrimmed = originalBody.trim();
    const isOriginalHTML =
      originalTrimmed &&
      !originalTrimmed.startsWith("{") &&
      !originalTrimmed.startsWith("[");

    let htmlContent;

    if (isOriginalHTML) {
      // Original content is HTML - use it directly for proper rendering
      // But also check if user has made edits in EditorJS
      let editorContent = null;
      if (editorRef.current && editorRef.current.getEditorInstance) {
        try {
          const editorInstance = editorRef.current.getEditorInstance();
          if (editorInstance) {
            const savedData = await editorInstance.save();
            // Check if editor has meaningful content (more than just the converted HTML)
            if (savedData.blocks && savedData.blocks.length > 0) {
              editorContent = JSON.stringify(savedData);
            }
          }
        } catch (error) {
          console.error("Error getting editor content:", error);
        }
      }

      // Use original HTML for preview (preserves full formatting)
      htmlContent = originalBody;
    } else {
      // Original content is JSON or empty - get latest from EditorJS
      let bodyContent = originalBody;
      if (editorRef.current && editorRef.current.getEditorInstance) {
        try {
          const editorInstance = editorRef.current.getEditorInstance();
          if (editorInstance) {
            const savedData = await editorInstance.save();
            bodyContent = JSON.stringify(savedData);
          }
        } catch (error) {
          console.error("Error getting editor content:", error);
        }
      }

      if (!bodyContent) {
        setPreviewContent("<p>No content to preview</p>");
        setShowPreviewModal(true);
        return;
      }

      // Convert JSON to HTML
      const trimmed = bodyContent.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        htmlContent = convertEditorJSToHTML(bodyContent);
      } else {
        htmlContent = bodyContent;
      }
    }

    if (!htmlContent) {
      setPreviewContent("<p>No content to preview</p>");
    } else {
      // Replace ~GENERAL_HEADER~ and ~GENERAL_FOOTER~ with their template content
      if (generalTemplates.GENERAL_HEADER) {
        const headerHtml = convertEditorJSToHTML(
          generalTemplates.GENERAL_HEADER
        );
        htmlContent = htmlContent.replace(/~GENERAL_HEADER~/g, headerHtml);
      } else {
        htmlContent = htmlContent.replace(/~GENERAL_HEADER~/g, "");
      }
      if (generalTemplates.GENERAL_FOOTER) {
        const footerHtml = convertEditorJSToHTML(
          generalTemplates.GENERAL_FOOTER
        );
        htmlContent = htmlContent.replace(/~GENERAL_FOOTER~/g, footerHtml);
      } else {
        htmlContent = htmlContent.replace(/~GENERAL_FOOTER~/g, "");
      }

      // Replace template variables with example data from jsonExample,
      // merged with real accountancy values from general parameters.
      const accountancyValues = await getAccountancyValues(activeCountry);
      const previewVariables = {
        ...availableVariables,
        generalInfo: {
          ...availableVariables.generalInfo,
          ...accountancyValues,
        },
      };
      htmlContent = replaceVariablesWithExampleData(
        htmlContent,
        previewVariables
      );
      setPreviewContent(wrapEmailHtml(htmlContent));
    }

    setShowPreviewModal(true);
    setPreviewing(true);
  };

  const handleClosePreviewModal = () => {
    setPreviewContent("");
    setPreviewing(false);
    setShowPreviewModal(false);
  };

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

  const openSendExampleModal = () => {
    setSendExampleEmail("");
    setSendExampleError("");
    setShowSendExampleModal(true);
  };

  const closeSendExampleModal = () => {
    setSendExampleEmail("");
    setSendExampleError("");
    setShowSendExampleModal(false);
  };

  const handleSendExample = () => {
    const email = sendExampleEmail.trim();
    if (!email || !isValidEmail(email)) {
      setSendExampleError(
        t("MAIL_TEMPLATE_SEND_EXAMPLE_ERROR", {
          defaultValue: "Invalid email address",
        })
      );
      return;
    }

    const currentTemplate = templates[activeCountry] || {};
    if (!currentTemplate.id) {
      setSendExampleError(
        t("MAIL_TEMPLATE_SEND_EXAMPLE_NO_TEMPLATE", {
          defaultValue: "Please save the template before sending a test email",
        })
      );
      return;
    }

    setSendingExample(true);
    axios
      .post("/general/sendTestEmail", {
        email,
        mailTokenId: parseInt(id, 10),
        mailTemplateId: currentTemplate.id,
      })
      .then(() => {
        setSendingExample(false);
        closeSendExampleModal();
      })
      .catch((error) => {
        setSendingExample(false);
        setSendExampleError(
          error.response?.data?.message ||
            t("MAIL_TEMPLATE_SEND_EXAMPLE_ERROR", {
              defaultValue: "Failed to send test email",
            })
        );
      });
  };

  // Get countries with existing templates
  const countriesWithTemplates = Object.keys(templates)
    .map((countryId) => {
      const country = countries.find((c) => c.id === parseInt(countryId));
      return country ? { ...country, countryId: parseInt(countryId) } : null;
    })
    .filter(Boolean);

  // Get platform countries that don't have templates yet
  const availableCountries = countries.filter(
    (country) => country.platform === 1 && !templates[country.id]
  );

  const currentTemplate = templates[activeCountry] || {};
  const currentCountry = countries.find((c) => c.id === activeCountry);

  if (loading) {
    return (
      <MailTokenViewContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <ClipLoader size={50} color="#1BACE4" />
        </div>
      </MailTokenViewContainer>
    );
  }

  if (isNotFound) {
    return <NotFound />;
  }

  return (
    <>
      <MailTokenViewContainer className="add-edit-mailtemplate">
        <Header>
          <Title>
            #{mailToken.id} - {mailToken?.name}
          </Title>
          {mailToken?.description && (
            <Description>{mailToken.description}</Description>
          )}
        </Header>

        {(countriesWithTemplates.length > 0 ||
          availableCountries.length > 0) && (
          <TabContainer>
            {countriesWithTemplates.map((country) => (
              <Tab
                key={country.id}
                active={activeCountry === country.id}
                onClick={() => handleTabChange(country.id)}
              >
                {country.label}
              </Tab>
            ))}
            {availableCountries.length > 0 && (
              <div ref={dropdownRef}>
                <AddTabButton
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  title="Add country template"
                >
                  <img
                    src={addIcon}
                    alt="upload Analytical note."
                    style={{ width: "auto" }}
                  />
                </AddTabButton>
                {showCountryDropdown && (
                  <div className="country-dropdown-container">
                    <ul className="country-dropdown-list">
                      {availableCountries.map((item) => (
                        <li
                          key={item.id}
                          className="country-upload-list-item"
                          onMouseDown={() => handleAddCountry(item.id)}
                        >
                          <label className="country-label">
                            {t(item.label)}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </TabContainer>
        )}

        <ContentContainer>
          <FormSection>
            <GridRow>
              <FormRow>
                <div onFocusCapture={() => handleFieldFocus("subject")}>
                  <TextInput
                    key={`subject-${activeCountry}`}
                    id="subject"
                    type="text"
                    label={t("MAIL_TEMPLATE_SUBJECT")}
                    autoComplete="off"
                    isFloat
                    required
                    value={currentTemplate.subject || ""}
                    onChange={(e) =>
                      handleFieldChange("subject", e.target.value)
                    }
                    containerStyles={{ marginTop: "21px", width: "100%" }}
                    errorStyles={{ "font-style": "italic" }}
                    labelStyles={
                      formErrors[`${activeCountry}_subject`]
                        ? labelStylesError
                        : labelStyles
                    }
                    inputStyles={
                      formErrors[`${activeCountry}_subject`]
                        ? inputStylesError
                        : inputStyles
                    }
                    error={formErrors[`${activeCountry}_subject`]}
                    placeholder={t("SUBJECT")}
                  />
                </div>
              </FormRow>
            </GridRow>
            <GridRow>
              <FormRow>
                <div onFocusCapture={() => handleFieldFocus("toAddress")}>
                  <TextInput
                    key={`recipients-${activeCountry}`}
                    id="recipients"
                    type="text"
                    label={t("MAIL_TEMPLATE_RECIPIENTS")}
                    autoComplete="off"
                    isFloat
                    required
                    value={currentTemplate.toAddress || ""}
                    onChange={(e) =>
                      handleFieldChange("toAddress", e.target.value)
                    }
                    containerStyles={{ marginTop: "21px", width: "100%" }}
                    errorStyles={{ "font-style": "italic" }}
                    labelStyles={
                      formErrors[`${activeCountry}_toAddress`]
                        ? labelStylesError
                        : labelStyles
                    }
                    inputStyles={
                      formErrors[`${activeCountry}_toAddress`]
                        ? inputStylesError
                        : inputStyles
                    }
                    error={formErrors[`${activeCountry}_toAddress`]}
                    placeholder={t("RECIPIENTS")}
                  />
                </div>
              </FormRow>
            </GridRow>
            <GridRow column="1fr 1fr">
              <FormRow>
                <div onFocusCapture={() => handleFieldFocus("cc")}>
                  <TextInput
                    key={`cc-${activeCountry}`}
                    id="cc"
                    type="text"
                    label={t("MAIL_TEMPLATE_CC")}
                    autoComplete="off"
                    isFloat
                    value={currentTemplate.cc || ""}
                    onChange={(e) => handleFieldChange("cc", e.target.value)}
                    containerStyles={{ marginTop: "21px", width: "100%" }}
                    errorStyles={{ "font-style": "italic" }}
                    labelStyles={labelStyles}
                    inputStyles={inputStyles}
                    placeholder={t("CC")}
                  />
                </div>
              </FormRow>

              <FormRow>
                <div onFocusCapture={() => handleFieldFocus("cci")}>
                  <TextInput
                    key={`cci-${activeCountry}`}
                    id="cci"
                    type="text"
                    label={t("MAIL_TEMPLATE_BCC")}
                    autoComplete="off"
                    isFloat
                    value={currentTemplate.cci ?? currentTemplate.bcc ?? ""}
                    onChange={(e) => handleFieldChange("cci", e.target.value)}
                    containerStyles={{ marginTop: "21px", width: "100%" }}
                    errorStyles={{ "font-style": "italic" }}
                    labelStyles={labelStyles}
                    inputStyles={inputStyles}
                    placeholder={t("CCI")}
                  />
                </div>
              </FormRow>
            </GridRow>
            <GridRow>
              <FormRow>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontFamily: "Lato",
                    fontStyle: "normal",
                    fontSize: "1rem",
                    lineHeight: "1",
                    fontWeight: "bold",
                    color: formErrors[`${activeCountry}_body`]
                      ? "#d32f2f"
                      : "rgb(27, 172, 228)",
                  }}
                >
                  {t("MAIL_TEMPLATE_BODY")} *
                </label>
                {/* HTML editor tab + Format HTML button commented out:
                    raw HTML is edited via the EditorJS "raw" block
                    (CodeMirror) inside the visual editor.
                <EditorModeTabs>
                  ... (visual / html / format tabs) ...
                </EditorModeTabs>
                */}
                <div onFocus={() => handleFieldFocus("body")}>
                  <EditorJSText
                    key={`editor-${activeCountry}`}
                    ref={editorRef}
                    descriptionData={currentTemplate.body || ""}
                    setDescriptionData={(data) =>
                      handleFieldChange("body", data)
                    }
                  />
                </div>
                {formErrors[`${activeCountry}_body`] && (
                  <div
                    style={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {formErrors[`${activeCountry}_body`]}
                  </div>
                )}
              </FormRow>
            </GridRow>
          </FormSection>

          <SidebarContainer>
            <HeaderActions>
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handlePreview();
                }}
                disabled={previewing || !currentTemplate?.body}
                styles={{
                  color: "#666",
                  background: "#e0e0e0",
                  border: "1px solid #ccc",
                  padding: "0.6rem 1.5rem",
                  fontSize: "14px",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#d0d0d0",
                  },
                }}
              >
                {previewing ? (
                  <ClipLoader size={15} color="#666" />
                ) : (
                  t("MAIL_TEMPLATE_PREVIEW")
                )}
              </Button>
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  openSendExampleModal();
                }}
                disabled={!templates[activeCountry]?.id || sendingExample}
                styles={{
                  color: "#666",
                  background: "#e0e0e0",
                  border: "1px solid #ccc",
                  padding: "0.6rem 1.5rem",
                  fontSize: "14px",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#d0d0d0",
                  },
                }}
              >
                {sendingExample ? (
                  <ClipLoader size={15} color="#666" />
                ) : (
                  t("MAIL_TEMPLATE_SEND_EXAMPLE")
                )}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                styles={{
                  color: "white",
                  background: "#1BACE4",
                  border: "1px solid #1BACE4",
                  padding: "0.6rem 1.5rem",
                  fontSize: "14px",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#1589b6",
                  },
                }}
              >
                {saving ? (
                  <ClipLoader size={15} color="#fff" />
                ) : (
                  t("MAIL_TEMPLATE_VALIDATE")
                )}
              </Button>
            </HeaderActions>
            <VariablesSection>
              <h3>{t("MAIL_TEMPLATE_HELPER_TITLE")}</h3>

              <div style={{ marginBottom: "1.5rem" }}>
                <VariableCategory>
                  {t("MAIL_TEMPLATE_HELPER_GENERAL", {
                    defaultValue: "General variables",
                  })}
                </VariableCategory>
                <VariablesList>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(
                        e,
                        "GENERAL_HEADER",
                        false,
                        false,
                        true,
                        true
                      )
                    }
                    title={t("VARIABLE_TOOLTIP_GENERAL_HEADER", {
                      defaultValue:
                        "Includes the content of the GENERAL_HEADER template for the same country. Click only works when body field is focused.",
                    })}
                  >
                    • ~GENERAL_HEADER~
                  </VariableItem>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(
                        e,
                        "GENERAL_FOOTER",
                        false,
                        false,
                        true,
                        true
                      )
                    }
                    title={t("VARIABLE_TOOLTIP_GENERAL_FOOTER", {
                      defaultValue:
                        "Includes the content of the GENERAL_FOOTER template for the same country. Click only works when body field is focused.",
                    })}
                  >
                    • ~GENERAL_FOOTER~
                  </VariableItem>
                </VariablesList>
              </div>

              {/* Recipients Section - for Recipients, CC, BCC fields */}
              <div style={{ marginBottom: "1.5rem" }}>
                <VariableCategory>
                  {t("MAIL_TEMPLATE_HELPER_RECIPIENTS")}
                </VariableCategory>
                <VariablesList>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(e, "CONCERNED_CONTACT", false, true)
                    }
                    title={t("VARIABLE_TOOLTIP_CONCERNED_CONTACT")}
                  >
                    • ~CONCERNED_CONTACT~
                  </VariableItem>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(e, "INTERNAL_SALESMEN", false, true)
                    }
                    title={t("VARIABLE_TOOLTIP_INTERNAL_SALESMEN")}
                  >
                    • ~INTERNAL_SALESMEN~
                  </VariableItem>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(e, "INTERNAL_STUDIES", false, true)
                    }
                    title={t("VARIABLE_TOOLTIP_INTERNAL_STUDIES")}
                  >
                    • ~INTERNAL_STUDIES~
                  </VariableItem>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(e, "INTERNAL_OPERATIONS", false, true)
                    }
                    title={t("VARIABLE_TOOLTIP_INTERNAL_OPERATIONS")}
                  >
                    • ~INTERNAL_OPERATIONS~
                  </VariableItem>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(e, "INTERNAL_FINANCE", false, true)
                    }
                    title={t("VARIABLE_TOOLTIP_INTERNAL_FINANCE")}
                  >
                    • ~INTERNAL_FINANCE~
                  </VariableItem>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(e, "INTERNAL_CUSTOMER", false, true)
                    }
                    title={t("VARIABLE_TOOLTIP_INTERNAL_CUSTOMER")}
                  >
                    • ~INTERNAL_CUSTOMER~
                  </VariableItem>
                  <VariableItem
                    onMouseDown={(e) =>
                      handleVariableClick(
                        e,
                        "INTERNAL_ALL_COUNTRY_ADMINS",
                        false,
                        true
                      )
                    }
                    title={t("VARIABLE_TOOLTIP_INTERNAL_ALL_COUNTRY_ADMINS")}
                  >
                    • ~INTERNAL_ALL_COUNTRY_ADMINS~
                  </VariableItem>
                </VariablesList>
              </div>

              {/* App Content Section - for Subject, Body fields */}
              <div>
                <VariableCategory>
                  {t("MAIL_TEMPLATE_HELPER_APP_CONTENT")}
                </VariableCategory>
                {Object.keys(availableVariables).length > 0 ? (
                  (() => {
                    // Helper to check if value is a nested object (not null, not array, is object)
                    const isObject = (val) =>
                      val !== null &&
                      typeof val === "object" &&
                      !Array.isArray(val);

                    // Toggle node expansion
                    const toggleNode = (nodeKey) => {
                      setExpandedNodes((prev) => ({
                        ...prev,
                        [nodeKey]: !prev[nodeKey],
                      }));
                    };

                    // Render a leaf variable (clickable to insert)
                    const renderLeafVariable = (key, path) => (
                      <VariableItem
                        key={`${path}.${key}`}
                        onMouseDown={(e) =>
                          handleVariableClick(e, `${path}.${key}`, false)
                        }
                        title={`Click to insert {${path}.${key}}`}
                      >
                        • {key}
                      </VariableItem>
                    );

                    // Render array node (collection)
                    const renderArrayNode = (
                      name,
                      items,
                      path,
                      parentName = null
                    ) => {
                      const nodeKey = path;
                      const isExpanded = expandedNodes[nodeKey] !== false; // Default expanded
                      const label = parentName
                        ? `${parentName} - ${name} - ${t(
                            "VARIABLE_TITLE_COLLECTION"
                          )}`
                        : `${name} - ${t("VARIABLE_TITLE_COLLECTION")}`;

                      // Get attributes from first item if array of objects
                      const firstItem =
                        Array.isArray(items) &&
                        items.length > 0 &&
                        isObject(items[0])
                          ? items[0]
                          : null;

                      // Separate simple attributes from nested objects within array items
                      const simpleAttrs = [];
                      const nestedObjects = [];

                      if (firstItem) {
                        Object.entries(firstItem).forEach(([key, value]) => {
                          if (isObject(value)) {
                            nestedObjects.push({ key, value });
                          } else {
                            simpleAttrs.push(key);
                          }
                        });
                      }

                      return (
                        <TreeNode key={nodeKey} depth={0}>
                          <TreeNodeHeader
                            isRoot
                            onClick={() => toggleNode(nodeKey)}
                          >
                            <TreeNodeIcon>
                              {isExpanded ? "▼" : "▶"}
                            </TreeNodeIcon>
                            <TreeNodeLabel isRoot>{label}</TreeNodeLabel>
                          </TreeNodeHeader>
                          {isExpanded && (
                            <TreeNodeChildren>
                              <VariablesList>
                                {firstItem ? (
                                  <>
                                    {/* Simple attributes */}
                                    {simpleAttrs.map((attr) =>
                                      renderLeafVariable(attr, path)
                                    )}
                                    {/* Nested objects - render as expandable sub-nodes */}
                                    {nestedObjects.map(({ key, value }) => (
                                      <div key={`${path}.${key}`}>
                                        {renderNestedObjectInArray(
                                          `${path}.${key}`,
                                          value,
                                          key
                                        )}
                                      </div>
                                    ))}
                                  </>
                                ) : (
                                  items.map((attr) =>
                                    renderLeafVariable(attr, path)
                                  )
                                )}
                              </VariablesList>
                            </TreeNodeChildren>
                          )}
                        </TreeNode>
                      );
                    };

                    // Render nested object within array item (e.g., planning.destination)
                    const renderNestedObjectInArray = (
                      path,
                      obj,
                      displayName
                    ) => {
                      const nodeKey = path;
                      const isExpanded = expandedNodes[nodeKey] !== false; // Default expanded

                      return (
                        <TreeNode key={nodeKey} depth={1}>
                          <TreeNodeHeader onClick={() => toggleNode(nodeKey)}>
                            <TreeNodeIcon>
                              {isExpanded ? "▼" : "▶"}
                            </TreeNodeIcon>
                            <TreeNodeLabel>{displayName}</TreeNodeLabel>
                          </TreeNodeHeader>
                          {isExpanded && (
                            <TreeNodeChildren>
                              <VariablesList>
                                {Object.keys(obj).map((attr) =>
                                  renderLeafVariable(attr, path)
                                )}
                              </VariablesList>
                            </TreeNodeChildren>
                          )}
                        </TreeNode>
                      );
                    };

                    // Render object node
                    const renderObjectNode = (name, obj, isRoot = true) => {
                      const nodeKey = name;
                      const isExpanded = expandedNodes[nodeKey] !== false; // Default expanded

                      // Separate simple attributes from nested arrays/objects
                      const simpleAttrs = [];
                      const nestedArrays = [];
                      const nestedObjects = [];

                      Object.entries(obj).forEach(([key, value]) => {
                        if (Array.isArray(value)) {
                          nestedArrays.push({ key, value });
                        } else if (isObject(value)) {
                          nestedObjects.push({ key, value });
                        } else {
                          simpleAttrs.push(key);
                        }
                      });

                      return (
                        <TreeNode key={nodeKey} depth={0}>
                          <TreeNodeHeader
                            isRoot={isRoot}
                            onClick={() => toggleNode(nodeKey)}
                          >
                            <TreeNodeIcon>
                              {isExpanded ? "▼" : "▶"}
                            </TreeNodeIcon>
                            <TreeNodeLabel isRoot={isRoot}>
                              {name}
                            </TreeNodeLabel>
                            {simpleAttrs.length > 0 && (
                              <TreeNodeBadge>
                                ({simpleAttrs.length} fields)
                              </TreeNodeBadge>
                            )}
                          </TreeNodeHeader>
                          {isExpanded && (
                            <TreeNodeChildren>
                              {/* Simple attributes */}
                              {simpleAttrs.length > 0 && (
                                <VariablesList>
                                  {simpleAttrs.map((attr) =>
                                    renderLeafVariable(attr, name)
                                  )}
                                </VariablesList>
                              )}

                              {/* Nested objects */}
                              {nestedObjects.map(({ key, value }) => (
                                <div key={`${name}.${key}`}>
                                  {renderObjectNode(
                                    `${name}.${key}`,
                                    value,
                                    false
                                  )}
                                </div>
                              ))}

                              {/* Nested arrays: "ParentName - ArrayName - Collection" */}
                              {nestedArrays.map(({ key, value }) =>
                                renderArrayNode(
                                  key,
                                  value,
                                  `${name}.${key}`,
                                  name
                                )
                              )}
                            </TreeNodeChildren>
                          )}
                        </TreeNode>
                      );
                    };

                    return Object.entries(availableVariables).map(
                      ([key, value]) => {
                        if (!value) return null;

                        if (Array.isArray(value)) {
                          // Root level array
                          return renderArrayNode(key, value, key);
                        } else if (isObject(value)) {
                          // Root level object
                          return renderObjectNode(key, value, true);
                        }

                        // Simple value at root (unlikely but handle it)
                        return (
                          <VariableItem
                            key={key}
                            onMouseDown={(e) =>
                              handleVariableClick(e, key, false)
                            }
                            title={`Click to insert {${key}}`}
                          >
                            • {key}
                          </VariableItem>
                        );
                      }
                    );
                  })()
                ) : (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#999",
                      fontStyle: "italic",
                    }}
                  >
                    {t("NO_VARIABLES_AVAILABLE")}
                  </p>
                )}
              </div>
            </VariablesSection>
          </SidebarContainer>
        </ContentContainer>
      </MailTokenViewContainer>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div
          className="preview-modal-overlay"
          onClick={handleClosePreviewModal}
        >
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>{t("MAIL_TEMPLATE_PREVIEW")}</h3>
              <button
                className="preview-modal-close"
                onClick={handleClosePreviewModal}
              >
                ×
              </button>
            </div>
            <div className="preview-modal-body">
              <iframe
                srcDoc={previewContent}
                title="Email Preview"
                className="preview-iframe"
              />
            </div>
          </div>
        </div>
      )}

      {/* Send Example Modal */}
      {showSendExampleModal && (
        <div
          className="send-example-modal-overlay"
          onClick={closeSendExampleModal}
        >
          <div
            className="send-example-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="send-example-modal-header">
              <h3>{t("MAIL_TEMPLATE_SEND_EXAMPLE")}</h3>
              <button
                className="send-example-modal-close"
                onClick={closeSendExampleModal}
              >
                ×
              </button>
            </div>
            <div className="send-example-modal-body">
              <TextInput
                label={t("MAIL_TEMPLATE_SEND_EXAMPLE_LABEL", {
                  defaultValue: "Email address",
                })}
                value={sendExampleEmail}
                onChange={(e) => {
                  setSendExampleEmail(e.target.value);
                  if (sendExampleError) setSendExampleError("");
                }}
                error={sendExampleError}
                autoComplete="off"
                type="text"
              />
            </div>
            <div className="send-example-modal-footer">
              <Button
                type="button"
                onClick={closeSendExampleModal}
                styles={{
                  color: "#666",
                  background: "#e0e0e0",
                  border: "1px solid #ccc",
                  padding: "0.6rem 1.5rem",
                  fontSize: "14px",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#d0d0d0",
                  },
                }}
              >
                {t("CANCEL", { defaultValue: "Cancel" })}
              </Button>
              <Button
                type="button"
                onClick={handleSendExample}
                disabled={sendingExample}
                styles={{
                  color: "white",
                  background: "#1BACE4",
                  border: "1px solid #1BACE4",
                  padding: "0.6rem 1.5rem",
                  fontSize: "14px",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#1589b6",
                  },
                }}
              >
                {sendingExample ? (
                  <ClipLoader size={15} color="#fff" />
                ) : (
                  t("VALIDATE", { defaultValue: "Validate" })
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

AddEditMailTemplate.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(AddEditMailTemplate);
